import { useRoute } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';

import GuardianCircleContent from '@/features/guardian-player/components/guardian-circle';
import { useGuardianCircle } from '@/features/guardian-player/hooks/use-guardian-circle';
import type { GuardianCircleMember } from '@/features/guardian-player/types';
import { useGuardianStackNavigation } from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';

const GuardianCircleScreen = () => {
  const navigation = useGuardianStackNavigation();
  const route = useRoute<GuardianStackScreenProps<'GuardianCircle'>['route']>();
  const { player } = route.params;

  const {
    firstName,
    members,
    guardians,
    activeAdultsCount,
    isPrimaryGuardian,
    isLoading,
    refetch,
  } = useGuardianCircle(player);

  // Drive the pull-to-refresh spinner ONLY from an actual user pull — not the query's
  // `isRefetching`, which also flips true on background refetches (e.g. the circle being
  // invalidated after a resend), leaving the RefreshControl spinner spuriously stuck.
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    Promise.resolve(refetch()).finally(() => setRefreshing(false));
  }, [refetch]);

  const handlePressMember = useCallback(
    (member: GuardianCircleMember) => {
      const guardian = guardians.find(g => g.id === member.id);
      if (!guardian) return;

      if (member.status === 'creator' || member.status === 'joined') {
        // Joined co-guardians open their profile. On tablet the master-detail view swaps
        // the right pane in-place (see GuardianCircleContent), so this push is phone-only.
        navigation.navigate('CoGuardianProfile', {
          player,
          guardianUid: guardian.guardianUid,
          isPrimaryGuardian,
        });
        return;
      }

      if (!guardian.invitationCode) return;

      // Pending invitation (waiting / expired / declined) → the Invitation Sent details.
      navigation.navigate('InvitationSent', {
        player,
        invitation: {
          ...guardian,
          contact: guardian.email ?? guardian.name,
        },
      });
    },
    [guardians, navigation, player, isPrimaryGuardian],
  );

  const handleInvite = useCallback(() => {
    navigation.navigate('InviteGuardian', { player });
  }, [navigation, player]);

  const handleLeave = useCallback(() => {
    const self = guardians.find(g => g.isSelf);
    if (!self) return;
    // The confirmation + leave API live on the dedicated Leave Circle screen.
    navigation.navigate('LeaveCircle', {
      player,
      guardianUid: self.guardianUid,
      since: self.since,
    });
  }, [guardians, navigation, player]);

  const handleRemoveMember = useCallback(
    (member: GuardianCircleMember) => {
      const guardian = guardians.find(g => g.id === member.id);
      if (!guardian?.guardianUid) return;
      // The confirmation + removal API live on the dedicated Remove Co-Guardian screen.
      navigation.navigate('RemoveCoGuardian', {
        player,
        guardianUid: guardian.guardianUid,
      });
    },
    [guardians, navigation, player],
  );

  return (
    <GuardianCircleContent
      player={player}
      firstName={firstName}
      members={members}
      activeAdultsCount={activeAdultsCount}
      isPrimaryGuardian={isPrimaryGuardian}
      onBack={navigation.goBack}
      onPressMember={handlePressMember}
      onInvite={handleInvite}
      onLeave={handleLeave}
      onRemoveMember={handleRemoveMember}
      isLoading={isLoading}
      onRefresh={handleRefresh}
      refreshing={refreshing}
    />
  );
};

export default GuardianCircleScreen;
